import { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getCompanyPreferences, updateCompanyPreferences } from '../api';
import {
  buildPreferencesPayload,
  companyPreferencesQueryKey,
  isBlank,
  isPreferencesComplete,
  normalizeCompanyPreferences,
  DEFAULT_PREFERENCE_LANGUAGE,
} from '../utils/companyPreferences';
import {
  readCompanyPreferences,
  writeCompanyPreferences,
} from '../utils/companyPreferencesStorage';

const FORBIDDEN_TOAST_ID = 'company-preferences-forbidden';

function getErrorMessage(error, fallback) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}

async function fetchCompanyPreferences(companyId) {
  try {
    const data = await getCompanyPreferences(companyId);
    const prefs = normalizeCompanyPreferences(data);
    writeCompanyPreferences(companyId, prefs);
    return prefs;
  } catch (error) {
    if (error?.response?.status === 404) {
      const prefs = normalizeCompanyPreferences(null);
      writeCompanyPreferences(companyId, prefs);
      return prefs;
    }
    throw error;
  }
}

export function useCompanyPreferences(companyId) {
  const queryClient = useQueryClient();
  const queryKey = companyPreferencesQueryKey(companyId);

  const query = useQuery({
    queryKey,
    queryFn: () => fetchCompanyPreferences(companyId),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
    initialData: () => readCompanyPreferences(companyId) || undefined,
    initialDataUpdatedAt: 0,
    retry: (failureCount, error) => {
      const status = error?.response?.status;
      if (status === 403 || status === 404) return false;
      return failureCount < 1;
    },
  });

  useEffect(() => {
    if (query.error?.response?.status === 403) {
      toast.error(
        getErrorMessage(
          query.error,
          'You do not have permission to view company preferences.',
        ),
        { id: FORBIDDEN_TOAST_ID },
      );
    }
  }, [query.error]);

  const updateMutation = useMutation({
    mutationFn: (payload) =>
      updateCompanyPreferences(companyId, buildPreferencesPayload(payload)),
    onSuccess: (data) => {
      const previous = queryClient.getQueryData(queryKey);
      const prefs = normalizeCompanyPreferences({
        ...previous,
        ...data,
      });
      if (isBlank(prefs.language)) {
        prefs.language = DEFAULT_PREFERENCE_LANGUAGE;
      }
      queryClient.setQueryData(queryKey, prefs);
      writeCompanyPreferences(companyId, prefs);
      toast.success('Preferences updated successfully!');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to update preferences'));
    },
  });

  const preferences = useMemo(
    () => (query.data ? normalizeCompanyPreferences(query.data) : undefined),
    [query.data],
  );

  return {
    preferences,
    isComplete: isPreferencesComplete(preferences),
    isPending: query.isPending,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    update: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
  };
}
